/* ─────────────────────────────────────────────────────────────────
   MLW Starter Bundle Lander — Klaviyo lead capture
   ─────────────────────────────────────────────────────────────────
   Creates/updates a Klaviyo profile and subscribes it to a list via
   Klaviyo's Client API v3. Captures email (always) and phone (SMS
   consent) from the hero reveal form.

   Paired with:
     • snippets/sbl-klaviyo-init.liquid — bootstraps init() and
       listens for the mlw-sbl:lead-submitted event to call
       subscribe() on the visitor's behalf.
     • sections/mlw-sbl-klaviyo.liquid — schema-driven opt-in
       per-page; passes apiKey / listId / brandSlug into the snippet.

   Modeled on assets/quiz-klaviyo.js. Errors are caught and logged —
   subscribe() never throws into the page flow.
   ───────────────────────────────────────────────────────────────── */

window.MLWSblKlaviyo = {
  _config: {},

  init: function (opts) {
    opts = opts || {};
    this._config = {
      apiKey: opts.apiKey,
      listId: opts.listId,
      brandSlug: opts.brandSlug || '',
      offerName: opts.offerName || '',
      smsConsent: opts.smsConsent !== false,
      defaultCountryCode: opts.defaultCountryCode || '+1'
    };
  },

  /* Best-effort E.164 normalization. Returns '' when we can't produce
     a confident E.164 number — Klaviyo rejects malformed SMS numbers,
     so it is safer to omit phone than to send garbage. */
  _normalizePhone: function (raw) {
    if (!raw) return '';
    var trimmed = String(raw).trim();
    if (trimmed.charAt(0) === '+') {
      var kept = '+' + trimmed.slice(1).replace(/[^0-9]/g, '');
      return kept.length >= 11 && kept.length <= 16 ? kept : '';
    }
    var digits = trimmed.replace(/[^0-9]/g, '');
    var cc = this._config.defaultCountryCode || '+1';
    if (cc === '+1') {
      if (digits.length === 10) return '+1' + digits;
      if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
      return '';
    }
    // Non-US default: prepend the configured country code if it looks bare.
    if (digits.length >= 8 && digits.length <= 14) return cc + digits;
    return '';
  },

  subscribe: function (payload) {
    payload = payload || {};
    var config = this._config;
    var apiKey = config.apiKey;
    var listId = config.listId;
    var email = (payload.email || '').trim();
    if (!apiKey || !listId || !email) return;

    var phone = config.smsConsent ? this._normalizePhone(payload.phone) : '';

    var properties = {
      lead_source: config.brandSlug || '',
      offer: config.offerName || '',
      landing_page: (typeof window !== 'undefined' && window.location) ? window.location.pathname : ''
    };

    var profileAttributes = {
      email: email,
      properties: properties
    };
    if (phone) profileAttributes.phone_number = phone;

    function checkResponse(label) {
      return function (response) {
        if (!response.ok) {
          response.text().then(function (body) {
            console.error('[MLWSblKlaviyo] ' + label + ' API error', response.status, body);
          });
        } else {
          console.info('[MLWSblKlaviyo] ' + label + ' API success', response.status);
        }
        return response;
      };
    }

    console.info('[MLWSblKlaviyo] subscribe called', { email: email, hasPhone: !!phone, listId: listId });

    try {
      // Create / update profile — company_id MUST be a query param
      // (Client API auth), not in the body.
      fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(apiKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: { type: 'profile', attributes: profileAttributes }
        })
      }).then(checkResponse('profiles'));

      // Subscribe to list. The /client/subscriptions/ endpoint handles
      // consent implicitly from the identifiers on the inline profile —
      // do NOT add a `subscriptions` key on the profile (Klaviyo rejects
      // it as an invalid field on the profile resource at this revision).
      // Email is subscribed from `email`; SMS is subscribed from
      // `phone_number` when the list has SMS opt-in enabled.
      var subProfileAttributes = { email: email, properties: properties };
      if (phone) subProfileAttributes.phone_number = phone;

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Starter Bundle Lander - ' + (config.brandSlug || 'unknown'),
              profile: { data: { type: 'profile', attributes: subProfileAttributes } }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        })
      }).then(checkResponse('subscriptions'));
    } catch (err) {
      console.warn('[MLWSblKlaviyo] Subscribe failed:', err);
    }
  }
};
